import React, { useContext, useEffect, useMemo, useState } from 'react'
import type { BrowserProductQueryQuery } from '@generated/graphql'
import { HeaderSizeContext } from 'src/Layout'
import { parseItemListElement } from 'src/utils/product/parseItemListElement'
import { useProductsQuery } from 'src/sdk/product/useProductsQuery'
import { Skeleton } from '@acctglobal/skeleton'
import useWindowDimensions from 'src/hooks/useWindowDimensions'
import { useDetailsSpecificationsContext } from 'src/contexts/details-specification-context'
import ProductInfoTab from 'src/components/product/ProdutInfoTab/ProductInfoTab'
import { isNewPDP } from 'src/utils/isNewPDP'

import AboutCollection from './AboutCollection'
import ProductShelfPDP from '../ProductShelfPDP/ProductShelfPDP'
import ProductSpecifications from './ProductSpecifications'
import type { IOProduct } from '../PlpSellerStore/types'
import {
  completeLookFacets,
  filterSpecification,
  getFilteredProductList,
  hasRelatedProducts,
} from './utils/utils'
import InsiderShelfSimilaresPdp from '../InsiderShelfSimilaresPdp'
import InsiderShelfRelatedProductsByNavigation from '../InsiderShelfRelatedProductsByNavigation'
import { useCmsSectionData } from 'src/hooks/useCmsSectionData'
import InsiderShelfUserHistory from '../InsiderShelfByUserHistory'
import InsiderBuyTogether from '../InsiderBuyTogether'

import './styles.scss'

interface Props {
  product: BrowserProductQueryQuery['product']
  buyTogetherProducts: IOProduct[]
}

export interface AnchorOffset {
  top: number
}

const ADJUST_OFFSET = 40

const AnchorMenu = ({ product, buyTogetherProducts }: Props) => {
  const {
    id,
    breadcrumbList: { itemListElement },
    isVariantOf: { additionalProperty },
  } = product

  const isPDPLife = itemListElement.some(
    (breadCrumbItem) => breadCrumbItem.name === 'Life'
  )

  const [isLoading, setLoading] = useState(true)
  const [anchorOffset, setAnchorOffset] = useState<AnchorOffset>()
  const { isWindowBiggerThanTablet } = useWindowDimensions()
  const headerSizeContext = useContext(HeaderSizeContext)
  const { detailsSpecifications } = useDetailsSpecificationsContext()

  useEffect(() => {
    setLoading(false)
    headerSizeContext?.headerSize &&
      setAnchorOffset({ top: -(headerSizeContext?.headerSize + ADJUST_OFFSET) })
  }, [headerSizeContext])

  // Get Global Data For Insider Shelfs
  const { data: relatedData } = useCmsSectionData(
    'InsiderShelfRelatedProductsByNavigation'
  )
  const { data: historyData } = useCmsSectionData('InsiderShelfUserHistory')
  const { data: similaresData } = useCmsSectionData('InsiderShelfSimilaresPdp')
  const { data: insiderBuyTogetherData } =
    useCmsSectionData('InsiderBuyTogether')

  const selectedFacets = parseItemListElement(itemListElement)
  const collectionName = filterSpecification(
    additionalProperty,
    'Coleção'
  )?.[0]?.value?.toString()

  const checkColecao = isNewPDP
    ? filterSpecification(additionalProperty, 'Coleção').length > 0
      ? collectionName
          ?.toString()
          ?.normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/ /g, '-')
          .toLowerCase()
      : ''
    : filterSpecification(additionalProperty, 'Coleção').length > 0
    ? filterSpecification(additionalProperty, 'Coleção')[0]
        .value?.toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ /g, '-')
        .toLowerCase()
    : ''

  let completeLook: string | undefined
  let relatedProducts: string | undefined

  const productList = useProductsQuery({
    first: 6,
    after: '0',
    sort: 'release_desc',
    term: '',
    selectedFacets: completeLookFacets(completeLook, checkColecao),
  })

  const relatedProductList = useProductsQuery({
    first: 6,
    after: '0',
    sort: 'release_desc',
    term: '',
    selectedFacets: hasRelatedProducts(relatedProducts, selectedFacets),
  })

  const products = useMemo(
    () => getFilteredProductList(productList, id),
    [productList, id]
  )

  const fullRelatedProductList = useMemo(
    () => getFilteredProductList(relatedProductList, id),
    [relatedProductList, id]
  )

  const notFullCollection = checkColecao !== '' || completeLook
  const buyTogetherProductsValidate = buyTogetherProducts.filter(
    (buyTogetherProduct) => buyTogetherProduct?.productId !== product?.productId
  )

  const hasSpecification = (
    product: BrowserProductQueryQuery['product'],
    specName: string
  ): boolean => {
    const allSpecificationsGroup = product?.specificationGroups?.find(
      (group) => group.name === 'allSpecifications'
    )

    if (!allSpecificationsGroup || !Array.isArray(allSpecificationsGroup.specifications)) {
      return false
    }

    return allSpecificationsGroup.specifications.some(
      (spec) =>
        spec?.name?.toLowerCase() === specName.toLowerCase() &&
        Array.isArray(spec.values) &&
        spec.values.length > 0
    )
  }

  const hasStone = hasSpecification(product, 'Pedras')
  const hasMaterial = hasSpecification(product, 'Material')

  return isLoading ? (
    <Skeleton backgroundColor="#F4F4F4" height={400} animation />
  ) : (
    <div>
      <section className="anchor-below-container">
        {isWindowBiggerThanTablet && buyTogetherProductsValidate.length > 0 ? (
          <></>
        ) : (
          notFullCollection &&
          products &&
          products.length !== 0 && (
            <>
              <div className="anchor-sub-title finish-look-section">
                <span
                  id="complete-o-look"
                  className="fake-anchor"
                  style={anchorOffset}
                />
                {isNewPDP ? (
                  <h2>COMPLETE O LOOK</h2>
                ) : (
                  collectionName && (
                    <h2>CONHEÇA MAIS DA COLEÇÃO {collectionName}</h2>
                  )
                )}
              </div>
              <div className="shelf-container">
                <ProductShelfPDP products={products} />
              </div>
            </>
          )
        )}
        {isNewPDP ? (
          <ProductInfoTab
            product={product}
            anchorOffset={anchorOffset}
            isPDPLife={isPDPLife}
          />
        ) : (
          <>
            <div className="anchor-sub-title anchor-sub-title-details">
              <span
                id="product-details"
                className="fake-anchor"
                style={anchorOffset}
              />
              <ProductSpecifications
                product={product}
                isPDPLife={isPDPLife}
                hasStone={hasStone}
                hasMaterial={hasMaterial}
              />
              {detailsSpecifications && detailsSpecifications.length >= 1 && (
                <AboutCollection
                  anchorOffset={anchorOffset}
                  isPDPLife={isPDPLife}
                />
              )}
            </div>
          </>
        )}
        <div className="anchor-sub-title">
          <span
            id="relationships-products"
            className="fake-anchor"
            style={anchorOffset}
          />
          PRODUTOS RELACIONADOS
        </div>

        <div className="shelf-container">
          <ProductShelfPDP
            products={fullRelatedProductList}
            isRelatedProducts
          />
        </div>
        {insiderBuyTogetherData && (
          <div className="shelf-container-buy-together">
            <InsiderBuyTogether
              campaignId={insiderBuyTogetherData.campaignId ?? ''}
              campaignIdForMobile={
                insiderBuyTogetherData.campaignIdForMobile ?? ''
              }
              campaignIdForTablet={
                insiderBuyTogetherData.campaignIdForTablet ?? ''
              }
              defaultCollectionId={
                insiderBuyTogetherData.defaultCollectionId ?? ''
              }
              title={insiderBuyTogetherData.title ?? ''}
              headingTitle={
                (insiderBuyTogetherData.headingTitle ?? 'h2') as HeadingLevel
              }
              fontFamily={insiderBuyTogetherData.fontFamily ?? ''}
              fallbackData={{ product }}
              productId={product.id}
            />
          </div>
        )}
        {similaresData && (
          <div className="shelf-container-insider">
            <InsiderShelfSimilaresPdp
              campaignId={similaresData.campaignId ?? ''}
              campaignIdForMobile={similaresData.campaignIdForMobile ?? ''}
              campaignIdForTablet={similaresData.campaignIdForTablet ?? ''}
              defaultCollectionId={similaresData.defaultCollectionId ?? ''}
              title={similaresData.title ?? ''}
              headingTitle={
                (similaresData.headingTitle ?? 'h2') as HeadingLevel
              }
              fontFamily={similaresData.fontFamily ?? ''}
            />
          </div>
        )}
        {relatedData && (
          <div className="shelf-container-insider">
            <InsiderShelfRelatedProductsByNavigation
              campaignId={relatedData.campaignId ?? ''}
              campaignIdForMobile={relatedData.campaignIdForMobile ?? ''}
              campaignIdForTablet={relatedData.campaignIdForTablet ?? ''}
              defaultCollectionId={relatedData.defaultCollectionId ?? ''}
              title={relatedData.title ?? ''}
              headingTitle={(relatedData.headingTitle ?? 'h2') as HeadingLevel}
              fontFamily={relatedData.fontFamily ?? ''}
            />
          </div>
        )}
        {historyData && (
          <div className="shelf-container-insider">
            <InsiderShelfUserHistory
              defaultCollectionId={historyData.defaultCollectionId ?? ''}
              title={historyData.title ?? ''}
              headingTitle={(historyData.headingTitle ?? 'h2') as HeadingLevel}
              fontFamily={historyData.fontFamily}
              campaignId={historyData.campaignId}
              campaignIdForMobile={historyData.campaignIdForMobile}
              campaignIdForTablet={historyData.campaignIdForTablet}
            />
          </div>
        )}
      </section>
    </div>
  )
}

export default AnchorMenu
